/*
 * File: config.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description:
 * Configuration for the axios http requests and responses
 *
 * We catch the JWT in each response here and pass it to the
 * LoginStore for storage
 *
 * Still having issues with Vapor server returning readable status codes
 * These two sites return valid status codes and axios is able to read them
 * so it doesn't seem to be an axios issue, rather a vapor issue,
 * more specifically a cors issue on the vapor server:
 *
 * https://api.ctl.io/v2/authentication/login
 * https://authenticate.macpractice.net/api/account/dns/account
 *
 * These urls can be tested by pasting them in the get object param in Http.js
 */

import axios from "axios";
import LoginStore from "../services/login/Store";
// default headers
axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.headers.common["Accept"] = "application/json";

export const Config = {
  apiBaseUrl: "/api/macpractice/"
};

let appStore = null;

/**
 *  Set the app store used by the axios interceptors.
 * @param {*} store The store to use.
 */
export function setInterceptorStore(store) {
  appStore = store;
}

/**
 * Get the http request/response error and update the state for the app to handle
 *
 * @param {object}
 */
const _handleError = error => {
  appStore.apiResponseError(error);
};

// request/response interceptors
axios.interceptors.request.use(
  // request success
  function(config) {
    // need to fetch the jwt on each request
    if (config.url.includes("macpractice") && config.url.endsWith("login") === false) {      
      config.headers.authorization = 'Bearer ' + LoginStore.getJwt();
    }

    return config;
  },

  // request error
  function(error) {
    _handleError(error);
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  // response successful
  function(response) {
    // new jwt issued so update it in the store
    if (!response.config.url.includes("zippopotam")) {
      try {
        const token = response.headers.authorization;
        if (token && token.toLowerCase().startsWith('bearer ')) {
          const parts = token.split(" ");
          if (parts.length > 1) {
            LoginStore.setJwt(parts[1]);
          }
        } else {
          _handleError("No JWT in the response header");
        }
      } catch (error) {
        console.error("API Response Error: " + JSON.stringify(error));
      }
    }
    return response;
  },

  // response error.
  function(error) {
    try {
      switch (error.response.status) {
        case 401:
          if (error.config.url.includes("login")) {
            // The login handling code expects a response in the case of an unauthorized attempt.
            // Here we handle the error before it reaches that code and send along the response object as a "success".
            return Promise.resolve(error.response);
          }
          _handleError(error);
          break;

        case 400:
          _handleError(error);
          break;

        case 404:
          if (error.config.url.includes("zippopotam")) {
            break;
          }
          _handleError(error);
          break;

        default:
          console.error("Response Error", JSON.stringify(error));
          _handleError(error);
      }
    } catch (e) {
      console.error("Can't read error status: " + e);
    }
    return Promise.reject(error);
  }
);
